import { useCallback, useEffect, useRef, useState } from 'react';
import { getAnthropicUsageDetails } from '../utils/anthropic-usage-adapter';
import { employeeDB } from '../utils/employee-indexeddb';
import {
  getOrganizationUsageDetails,
  type UsageDetailsData
} from '../utils/openai-api';
import {
  getAnthropicApiKey,
  getApiKey,
  readAdminCredentialRevision
} from '../utils/storage';
import { mergeUsageDetailsData } from '../utils/usage-merge';
import {
  evaluateDailyUserBudgets,
  formatUtcDateKey,
  type DailyBudgetEvaluation,
  type DailyBudgetIdentityMapping
} from '../utils/daily-user-budget';
import {
  DAILY_COST_MONITOR_COMPLETION_VERSION,
  publishDailyCostMonitorCompletion,
  resetDailyCostMonitorCompletionTransportForTests
} from '../utils/daily-cost-monitor-events';

export const DAILY_CHECK_INTERVAL_MS = 60 * 60 * 1000;
export const DAILY_CHECK_RETRY_BASE_MS = 60 * 1000;
const DAILY_CHECK_RETRY_MAX_MS = 60 * 60 * 1000;
const MONITOR_STATE_STORAGE_KEY = 'dailyCostMonitorStateV1';
const MONITOR_LEASE_STORAGE_KEY = 'dailyCostMonitorLeaseV1';
const MONITOR_LOCK_NAME = 'usage-dashboard-daily-cost-monitor';
const MONITOR_LEASE_DURATION_MS = 5 * 60 * 1000;
const MONITOR_LEASE_HEARTBEAT_MS = Math.floor(MONITOR_LEASE_DURATION_MS / 3);
const MONITOR_LEASE_CONFIRM_MS = 25;
const MONITOR_LOCK_RETRY_MS = 1_000;
const systemNow = () => new Date();
const monitorOwnerId = globalThis.crypto?.randomUUID?.()
  ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const nextScheduledCheckAt = (completedAt: Date): number => {
  const nextUtcDay = Date.UTC(
    completedAt.getUTCFullYear(),
    completedAt.getUTCMonth(),
    completedAt.getUTCDate() + 1
  );
  return Math.min(completedAt.getTime() + DAILY_CHECK_INTERVAL_MS, nextUtcDay + 1_000);
};

export type DailyCostMonitorStatus = 'idle' | 'checking' | 'ready' | 'error';

export interface UseDailyCostMonitorOptions {
  enabled?: boolean;
  hasOpenAiKey: boolean;
  hasAnthropicKey: boolean;
  credentialRevision?: string;
  threshold: number;
  onEvaluation: (evaluation: DailyBudgetEvaluation) => void;
  now?: () => Date;
}

export interface UseDailyCostMonitorReturn {
  status: DailyCostMonitorStatus;
  error: string | null;
  lastSuccessfulCheck: Date | null;
  checkNow: () => Promise<void>;
}

interface PersistedMonitorState {
  utcDay: string;
  configurationKey: string;
  lastSuccessAt: number;
  lastAttemptAt: number;
  retryCount: number;
  retryNotBefore: number;
}

type MonitorRunOutcome =
  | {
      status: 'success';
      evaluation: DailyBudgetEvaluation;
      completedAt: Date;
      providerData: Partial<Record<'openai' | 'anthropic', UsageDetailsData>>;
    }
  | { status: 'partial' | 'error' | 'stale_day' | 'stale_credential'; message: string }
  | { status: 'skipped'; reason: 'not_due' | 'backoff' | 'offline' | 'locked'; lastSuccessAt?: number };

interface MonitorRunOptions {
  hasOpenAiKey: boolean;
  hasAnthropicKey: boolean;
  credentialRevision: string;
  threshold: number;
  now: () => Date;
  force: boolean;
}

let sharedRun: { key: string; promise: Promise<MonitorRunOutcome> } | null = null;
let memoryMonitorState: PersistedMonitorState | null = null;

const describeError = (value: unknown): string =>
  value instanceof Error ? value.message : String(value);

const getEvaluationKey = (evaluation: DailyBudgetEvaluation): string => JSON.stringify({
  date: evaluation.date,
  threshold: evaluation.threshold,
  exceeded: evaluation.exceeded.map(budget => [
    budget.id,
    budget.todayCost,
    budget.costQuality,
    budget.sources.map(source => [
      source.provider,
      source.projectId,
      source.apiKeyId,
      source.sourceIds,
      source.cost
    ])
  ])
});

const emptyProviderData = (): UsageDetailsData => ({
  modelUsage: [],
  apiKeyUsage: [],
  totalRequests: 0,
  totalCost: 0,
  totalContextTokens: 0,
  totalGeneratedTokens: 0,
  totalCachedTokens: 0,
  totalApiKeys: 0,
  displayedApiKeys: 0,
  projects: [],
  dailyUsage: [],
  dataWindow: {
    start: '',
    end: '',
    timezoneOffsetMinutes: 0,
    coverageHours: 0,
    expectedHours: 0
  }
});

const configuredEmptyProviderData = (
  hasOpenAiKey: boolean,
  hasAnthropicKey: boolean
): Partial<Record<'openai' | 'anthropic', UsageDetailsData>> => ({
  ...(hasOpenAiKey ? { openai: emptyProviderData() } : {}),
  ...(hasAnthropicKey ? { anthropic: emptyProviderData() } : {})
});

const getConfigurationKey = (
  hasOpenAiKey: boolean,
  hasAnthropicKey: boolean,
  threshold: number,
  credentialRevision: string
): string => `${hasOpenAiKey ? 'openai' : ''}|${hasAnthropicKey ? 'anthropic' : ''}|${threshold}|${credentialRevision}`;

const readMonitorState = (): PersistedMonitorState | null => {
  try {
    const raw = localStorage.getItem(MONITOR_STATE_STORAGE_KEY);
    if (!raw) return memoryMonitorState;
    const parsed = JSON.parse(raw) as Partial<PersistedMonitorState>;
    if (
      typeof parsed.utcDay !== 'string'
      || typeof parsed.configurationKey !== 'string'
      || !Number.isFinite(parsed.lastSuccessAt)
      || !Number.isFinite(parsed.lastAttemptAt)
      || !Number.isFinite(parsed.retryCount)
      || !Number.isFinite(parsed.retryNotBefore)
    ) {
      return memoryMonitorState;
    }
    memoryMonitorState = parsed as PersistedMonitorState;
    return memoryMonitorState;
  } catch {
    return memoryMonitorState;
  }
};

const writeMonitorState = (state: PersistedMonitorState): void => {
  memoryMonitorState = state;
  try {
    localStorage.setItem(MONITOR_STATE_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Monitoring still works in-memory when storage is unavailable.
  }
};

const getSkipReason = (
  state: PersistedMonitorState | null,
  utcDay: string,
  configurationKey: string,
  nowMs: number
): 'not_due' | 'backoff' | null => {
  if (!state || state.utcDay !== utcDay || state.configurationKey !== configurationKey) {
    return null;
  }
  if (nowMs < state.retryNotBefore) return 'backoff';
  return nowMs - state.lastSuccessAt < DAILY_CHECK_INTERVAL_MS ? 'not_due' : null;
};

const persistFailure = (
  previous: PersistedMonitorState | null,
  utcDay: string,
  configurationKey: string,
  failedAt: number
): void => {
  const sameScope = previous?.utcDay === utcDay
    && previous.configurationKey === configurationKey;
  const retryCount = sameScope ? previous.retryCount + 1 : 1;
  const backoff = Math.min(
    DAILY_CHECK_RETRY_MAX_MS,
    DAILY_CHECK_RETRY_BASE_MS * (2 ** Math.max(0, retryCount - 1))
  );
  const jitter = Math.floor(Math.random() * Math.min(10_000, backoff * 0.1));
  writeMonitorState({
    utcDay,
    configurationKey,
    lastSuccessAt: sameScope && previous ? previous.lastSuccessAt : 0,
    lastAttemptAt: failedAt,
    retryCount,
    retryNotBefore: failedAt + backoff + jitter
  });
};

const loadIdentityMappings = async (): Promise<DailyBudgetIdentityMapping[]> => {
  const employees = await employeeDB.getAllEmployees();
  return employees.flatMap(employee => {
    const base = {
      // IndexedDB's numeric auto-increment id is storage-local, not a stable
      // organization identity. Fall back to normalized email when employeeId is absent.
      employeeId: employee.employeeId,
      email: employee.email,
      displayName: employee.name || employee.email
    };
    const apiKeyIds = Array.from(new Set([
      ...(employee.apiKeyIds ?? []),
      ...(employee.apiKeyId ? [employee.apiKeyId] : [])
    ]));
    return [base, ...apiKeyIds.map(apiKeyId => ({ ...base, apiKeyId }))];
  });
};

const fetchAndEvaluate = async (
  options: MonitorRunOptions,
  startedAt: Date
): Promise<MonitorRunOutcome> => {
  const utcDay = formatUtcDateKey(startedAt);

  if (!Number.isFinite(options.threshold) || options.threshold < 0) {
    return {
      status: 'error',
      message: 'Daily usage monitor threshold is invalid; previous alerts were preserved'
    };
  }

  if (!options.hasOpenAiKey && !options.hasAnthropicKey) {
    return {
      status: 'success',
      evaluation: evaluateDailyUserBudgets([], { date: utcDay, threshold: options.threshold }),
      completedAt: startedAt,
      providerData: configuredEmptyProviderData(
        options.hasOpenAiKey,
        options.hasAnthropicKey
      )
    };
  }

  const startDate = new Date(Date.UTC(
    startedAt.getUTCFullYear(),
    startedAt.getUTCMonth(),
    startedAt.getUTCDate()
  ));
  if (startedAt.getTime() <= startDate.getTime()) {
    return {
      status: 'success',
      evaluation: evaluateDailyUserBudgets([], { date: utcDay, threshold: options.threshold }),
      completedAt: startedAt,
      providerData: configuredEmptyProviderData(
        options.hasOpenAiKey,
        options.hasAnthropicKey
      )
    };
  }
  const range = { startDate, endDate: startedAt, label: 'Today (UTC)' };
  const tasks: Array<Promise<{ provider: 'openai' | 'anthropic'; response: Awaited<ReturnType<typeof getOrganizationUsageDetails>> }>> = [];

  if (options.hasOpenAiKey) {
    const apiKey = getApiKey();
    if (!apiKey) {
      tasks.push(Promise.reject(new Error('OpenAI Admin API key is not configured')));
    } else {
      tasks.push(
        getOrganizationUsageDetails(
          apiKey,
          undefined,
          range,
          true,
          options.credentialRevision
        )
          .then(response => ({ provider: 'openai' as const, response }))
      );
    }
  }

  if (options.hasAnthropicKey) {
    const apiKey = getAnthropicApiKey();
    if (!apiKey) {
      tasks.push(Promise.reject(new Error('Anthropic Admin API key is not configured')));
    } else {
      tasks.push(
        getAnthropicUsageDetails(range, undefined, {
          apiKey,
          credentialRevision: options.credentialRevision
        }).then(response => ({ provider: 'anthropic' as const, response }))
      );
    }
  }

  const settled = await Promise.allSettled(tasks);
  const sources: UsageDetailsData[] = [];
  const providerData: Partial<Record<'openai' | 'anthropic', UsageDetailsData>> = {};
  const errors: string[] = [];

  for (const result of settled) {
    if (result.status === 'rejected') {
      errors.push(describeError(result.reason));
      continue;
    }

    const { provider, response } = result.value;
    if (!response?.success || !response.data) {
      errors.push(`${provider}: ${response?.error || 'usage request failed'}`);
      continue;
    }

    if (response.data.partialErrors?.length) {
      errors.push(...response.data.partialErrors.map((message: string) => `${provider}: ${message}`));
    }
    sources.push(response.data);
    providerData[provider] = response.data;
  }

  if (errors.length > 0 || sources.length !== tasks.length) {
    return {
      status: 'partial',
      message: errors.length > 0
        ? `Daily usage monitor is incomplete: ${errors.join(' | ')}`
        : 'Daily usage monitor is incomplete'
    };
  }

  let identityMappings: DailyBudgetIdentityMapping[];
  try {
    identityMappings = await loadIdentityMappings();
  } catch (cause) {
    return {
      status: 'error',
      message: `Daily usage monitor could not load employee mappings: ${describeError(cause)}`
    };
  }

  const completedAt = options.now();
  const completedDay = formatUtcDateKey(completedAt);
  if (completedDay !== utcDay) {
    return {
      status: 'stale_day',
      message: `Daily usage monitor crossed the UTC day boundary (${utcDay} to ${completedDay}); stale results were discarded`
    };
  }

  const merged = sources.length === 1 ? sources[0] : mergeUsageDetailsData(sources);
  return {
    status: 'success',
    evaluation: evaluateDailyUserBudgets(merged.apiKeyUsage, {
      date: utcDay,
      threshold: options.threshold,
      identityMappings
    }),
    completedAt,
    providerData
  };
};

interface MonitorLease {
  ownerId: string;
  expiresAt: number;
}

const readMonitorLease = (): MonitorLease | null => {
  try {
    const parsed = JSON.parse(localStorage.getItem(MONITOR_LEASE_STORAGE_KEY) ?? 'null');
    return parsed
      && typeof parsed.ownerId === 'string'
      && Number.isFinite(parsed.expiresAt)
      ? parsed as MonitorLease
      : null;
  } catch {
    return null;
  }
};

const runWithFallbackLease = async (
  callback: (ownsLock: () => boolean) => Promise<MonitorRunOutcome>
): Promise<MonitorRunOutcome> => {
  const nowMs = Date.now();
  const existing = readMonitorLease();
  if (existing && existing.expiresAt > nowMs) {
    return { status: 'skipped', reason: 'locked' };
  }

  try {
    localStorage.setItem(MONITOR_LEASE_STORAGE_KEY, JSON.stringify({
      ownerId: monitorOwnerId,
      expiresAt: nowMs + MONITOR_LEASE_DURATION_MS
    } satisfies MonitorLease));
  } catch {
    return callback(() => true);
  }

  // Give simultaneous contenders a chance to publish their claim, then only
  // the last confirmed owner proceeds. This is best-effort where Web Locks is unavailable.
  await new Promise(resolve => window.setTimeout(resolve, MONITOR_LEASE_CONFIRM_MS));
  const confirmedLease = readMonitorLease();
  if (
    !confirmedLease
    || confirmedLease.ownerId !== monitorOwnerId
    || confirmedLease.expiresAt <= Date.now()
  ) {
    return { status: 'skipped', reason: 'locked' };
  }

  let leaseLost = false;
  const ownsLease = (): boolean => {
    const currentLease = readMonitorLease();
    return !leaseLost
      && !!currentLease
      && currentLease.ownerId === monitorOwnerId
      && currentLease.expiresAt > Date.now();
  };
  const heartbeatId = window.setInterval(() => {
    try {
      const currentLease = readMonitorLease();
      if (
        currentLease?.ownerId === monitorOwnerId
        && currentLease.expiresAt > Date.now()
      ) {
        localStorage.setItem(MONITOR_LEASE_STORAGE_KEY, JSON.stringify({
          ownerId: monitorOwnerId,
          expiresAt: Date.now() + MONITOR_LEASE_DURATION_MS
        } satisfies MonitorLease));
      } else {
        leaseLost = true;
      }
    } catch {
      // The existing expiry remains the bounded fallback if renewal fails.
    }
  }, MONITOR_LEASE_HEARTBEAT_MS);

  try {
    const outcome = await callback(ownsLease);
    return !ownsLease()
      ? { status: 'skipped', reason: 'locked' }
      : outcome;
  } finally {
    window.clearInterval(heartbeatId);
    try {
      if (readMonitorLease()?.ownerId === monitorOwnerId) {
        localStorage.removeItem(MONITOR_LEASE_STORAGE_KEY);
      }
    } catch {
      // Lease expiry releases the fallback lock if storage cleanup fails.
    }
  }
};

const withCrossTabLock = async (
  callback: (ownsLock: () => boolean) => Promise<MonitorRunOutcome>
): Promise<MonitorRunOutcome> => {
  const lockManager = typeof navigator !== 'undefined'
    ? (navigator as Navigator & { locks?: { request: (...args: unknown[]) => Promise<MonitorRunOutcome | undefined> } }).locks
    : undefined;
  if (!lockManager?.request) return runWithFallbackLease(callback);

  try {
    const result = await lockManager.request(
      MONITOR_LOCK_NAME,
      { mode: 'exclusive', ifAvailable: true },
      async (lock: unknown) => lock ? callback(() => true) : undefined
    );
    return result ?? { status: 'skipped', reason: 'locked' };
  } catch (cause) {
    console.warn('Daily usage monitor Web Lock failed; using storage lease fallback', cause);
    return runWithFallbackLease(callback);
  }
};

const runMonitor = (options: MonitorRunOptions): Promise<MonitorRunOutcome> => {
  const startedAt = options.now();
  const utcDay = formatUtcDateKey(startedAt);
  const configurationKey = getConfigurationKey(
    options.hasOpenAiKey,
    options.hasAnthropicKey,
    options.threshold,
    options.credentialRevision
  );
  const runKey = `${utcDay}|${configurationKey}`;
  if (sharedRun?.key === runKey) return sharedRun.promise;

  const promise = (async (): Promise<MonitorRunOutcome> => {
    if (
      typeof navigator !== 'undefined'
      && 'onLine' in navigator
      && navigator.onLine === false
    ) {
      return { status: 'skipped', reason: 'offline' };
    }

    const initialState = readMonitorState();
    const initialSkipReason = options.force
      ? null
      : getSkipReason(initialState, utcDay, configurationKey, startedAt.getTime());
    if (initialSkipReason) {
      return {
        status: 'skipped',
        reason: initialSkipReason,
        lastSuccessAt: initialState?.lastSuccessAt
      };
    }

    return withCrossTabLock(async ownsLock => {
      // Another tab may have completed while this tab was waiting for the lock.
      const lockedState = readMonitorState();
      const lockedSkipReason = options.force
        ? null
        : getSkipReason(lockedState, utcDay, configurationKey, startedAt.getTime());
      if (lockedSkipReason) {
        return {
          status: 'skipped',
          reason: lockedSkipReason,
          lastSuccessAt: lockedState?.lastSuccessAt
        };
      }

      if (!ownsLock()) return { status: 'skipped', reason: 'locked' };

      writeMonitorState({
        utcDay,
        configurationKey,
        lastSuccessAt: lockedState?.utcDay === utcDay
          && lockedState.configurationKey === configurationKey
          ? lockedState.lastSuccessAt
          : 0,
        lastAttemptAt: startedAt.getTime(),
        retryCount: lockedState?.utcDay === utcDay
          && lockedState.configurationKey === configurationKey
          ? lockedState.retryCount
          : 0,
        retryNotBefore: lockedState?.utcDay === utcDay
          && lockedState.configurationKey === configurationKey
          ? lockedState.retryNotBefore
          : 0
      });

      try {
        const outcome = await fetchAndEvaluate(options, startedAt);
        if (!ownsLock()) return { status: 'skipped', reason: 'locked' };
        if (readAdminCredentialRevision() !== options.credentialRevision) {
          return {
            status: 'stale_credential',
            message: 'Admin credentials changed during the daily usage check; stale results were discarded'
          };
        }
        if (outcome.status === 'success') {
          writeMonitorState({
            utcDay,
            configurationKey,
            lastSuccessAt: outcome.completedAt.getTime(),
            lastAttemptAt: startedAt.getTime(),
            retryCount: 0,
            retryNotBefore: 0
          });
        } else if (outcome.status !== 'skipped') {
          persistFailure(
            lockedState,
            utcDay,
            configurationKey,
            options.now().getTime()
          );
        }
        return outcome;
      } catch (cause) {
        if (!ownsLock()) return { status: 'skipped', reason: 'locked' };
        if (readAdminCredentialRevision() !== options.credentialRevision) {
          return {
            status: 'stale_credential',
            message: 'Admin credentials changed during the daily usage check; stale failure state was discarded'
          };
        }
        persistFailure(
          lockedState,
          utcDay,
          configurationKey,
          options.now().getTime()
        );
        return {
          status: 'error',
          message: `Daily usage monitor failed: ${describeError(cause)}`
        };
      }
    });
  })().finally(() => {
    if (sharedRun?.promise === promise) sharedRun = null;
  });

  sharedRun = { key: runKey, promise };
  return promise;
};

/** Reset module state for isolated unit tests. */
export const resetDailyCostMonitorForTests = (): void => {
  sharedRun = null;
  memoryMonitorState = null;
  resetDailyCostMonitorCompletionTransportForTests();
  try {
    localStorage.removeItem(MONITOR_STATE_STORAGE_KEY);
    localStorage.removeItem(MONITOR_LEASE_STORAGE_KEY);
  } catch {
    // no-op
  }
};

/**
 * App-scope UTC daily budget monitor. It is independent of the visible tab,
 * selected provider, and chart date range, and it preserves prior alerts when
 * any configured provider returns incomplete data.
 */
export function useDailyCostMonitor({
  enabled = true,
  hasOpenAiKey,
  hasAnthropicKey,
  credentialRevision = 'initial',
  threshold,
  onEvaluation,
  now = systemNow
}: UseDailyCostMonitorOptions): UseDailyCostMonitorReturn {
  const [status, setStatus] = useState<DailyCostMonitorStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [lastSuccessfulCheck, setLastSuccessfulCheck] = useState<Date | null>(null);
  const [nextCheckAt, setNextCheckAt] = useState<number | null>(null);
  const mountedRef = useRef(true);
  const lastAppliedEvaluationRef = useRef<string | null>(null);
  const pendingEvaluationRef = useRef<{
    configuration: string;
    evaluation: DailyBudgetEvaluation;
  } | null>(null);
  const nowRef = useRef(now);
  const onEvaluationRef = useRef(onEvaluation);
  const lastRefreshDispatchRef = useRef<number | null>(null);
  const currentConfigurationRef = useRef('');
  const configurationToken = `${enabled}|${getConfigurationKey(
    hasOpenAiKey,
    hasAnthropicKey,
    threshold,
    credentialRevision
  )}`;
  currentConfigurationRef.current = configurationToken;

  useEffect(() => {
    nowRef.current = now;
    onEvaluationRef.current = onEvaluation;
  }, [now, onEvaluation]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const applyEvaluation = useCallback((evaluation: DailyBudgetEvaluation): string | null => {
    const evaluationKey = getEvaluationKey(evaluation);
    if (evaluationKey === lastAppliedEvaluationRef.current) return null;
    try {
      onEvaluationRef.current(evaluation);
      lastAppliedEvaluationRef.current = evaluationKey;
      return null;
    } catch (cause) {
      const message = `Daily usage alert persistence failed: ${describeError(cause)}`;
      console.error(message, cause);
      return message;
    }
  }, []);

  const runCheck = useCallback(async (force: boolean): Promise<void> => {
    const requestedConfiguration = `${enabled}|${getConfigurationKey(
      hasOpenAiKey,
      hasAnthropicKey,
      threshold,
      credentialRevision
    )}`;
    if (!enabled) {
      if (mountedRef.current) {
        setStatus('idle');
        setError(null);
        setNextCheckAt(null);
      }
      return;
    }

    if (
      pendingEvaluationRef.current
      && pendingEvaluationRef.current.configuration !== requestedConfiguration
    ) {
      pendingEvaluationRef.current = null;
    }
    if (pendingEvaluationRef.current) {
      const evaluationError = applyEvaluation(pendingEvaluationRef.current.evaluation);
      if (evaluationError) {
        setStatus('error');
        setError(evaluationError);
        setNextCheckAt(nowRef.current().getTime() + DAILY_CHECK_RETRY_BASE_MS);
        return;
      }
      pendingEvaluationRef.current = null;
      const state = readMonitorState();
      const successfulAt = state?.lastSuccessAt ? new Date(state.lastSuccessAt) : nowRef.current();
      setStatus(hasOpenAiKey || hasAnthropicKey ? 'ready' : 'idle');
      setError(null);
      setLastSuccessfulCheck(successfulAt);
      setNextCheckAt(nextScheduledCheckAt(successfulAt));
      return;
    }

    if (mountedRef.current) {
      setStatus('checking');
      setNextCheckAt(null);
    }

    const outcome = await runMonitor({
      hasOpenAiKey,
      hasAnthropicKey,
      credentialRevision,
      threshold,
      now: nowRef.current,
      force
    });
    if (
      !mountedRef.current
      || currentConfigurationRef.current !== requestedConfiguration
    ) return;

    switch (outcome.status) {
      case 'success':
        {
          const evaluationError = applyEvaluation(outcome.evaluation);
          if (evaluationError) {
            pendingEvaluationRef.current = {
              configuration: requestedConfiguration,
              evaluation: outcome.evaluation
            };
          } else {
            pendingEvaluationRef.current = null;
          }
          setStatus(evaluationError
            ? 'error'
            : hasOpenAiKey || hasAnthropicKey ? 'ready' : 'idle');
          setError(evaluationError);
          setNextCheckAt(evaluationError
            ? nowRef.current().getTime() + DAILY_CHECK_RETRY_BASE_MS
            : nextScheduledCheckAt(outcome.completedAt));
        }
        setLastSuccessfulCheck(outcome.completedAt);
        if (lastRefreshDispatchRef.current !== outcome.completedAt.getTime()) {
          lastRefreshDispatchRef.current = outcome.completedAt.getTime();
          publishDailyCostMonitorCompletion({
            type: 'complete',
            version: DAILY_COST_MONITOR_COMPLETION_VERSION,
            utcDay: outcome.evaluation.date,
            completedAt: outcome.completedAt.getTime(),
            credentialRevision,
            providers: outcome.providerData
          });
        }
        return;
      case 'partial':
      case 'error':
        setStatus('error');
        setError(outcome.message);
        setNextCheckAt(
          readMonitorState()?.retryNotBefore
            ?? nowRef.current().getTime() + DAILY_CHECK_RETRY_BASE_MS
        );
        return;
      case 'stale_day':
        setStatus('error');
        setError(outcome.message);
        setNextCheckAt(nowRef.current().getTime() + 1);
        return;
      case 'stale_credential':
        setStatus('idle');
        setError(null);
        setNextCheckAt(nowRef.current().getTime() + MONITOR_LOCK_RETRY_MS);
        return;
      case 'skipped':
        if (outcome.reason === 'offline') {
          setStatus('error');
          setError('Daily usage monitor is offline; the previous complete alerts were preserved');
          setNextCheckAt(null);
        } else if (outcome.reason === 'not_due') {
          setStatus(outcome.lastSuccessAt ? 'ready' : 'idle');
          setError(null);
          if (outcome.lastSuccessAt) {
            const lastSuccess = new Date(outcome.lastSuccessAt);
            setLastSuccessfulCheck(lastSuccess);
            setNextCheckAt(nextScheduledCheckAt(lastSuccess));
          }
        } else if (outcome.reason === 'backoff') {
          setStatus('error');
          setError(previous => previous
            ?? 'Daily usage monitor retry is delayed after an incomplete check; previous alerts were preserved');
          setNextCheckAt(
            readMonitorState()?.retryNotBefore
              ?? nowRef.current().getTime() + DAILY_CHECK_RETRY_BASE_MS
          );
        } else {
          // Another tab owns the check. Keep any existing error visible until a
          // complete result arrives through notification/storage synchronization.
          setStatus(previous => previous === 'checking' ? 'idle' : previous);
          setNextCheckAt(nowRef.current().getTime() + MONITOR_LOCK_RETRY_MS);
        }
        return;
    }
  }, [
    applyEvaluation,
    credentialRevision,
    enabled,
    hasAnthropicKey,
    hasOpenAiKey,
    threshold
  ]);

  useEffect(() => {
    if (!enabled) return;

    void runCheck(false);
    const catchUp = () => {
      void runCheck(false);
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') catchUp();
    };

    window.addEventListener('focus', catchUp);
    window.addEventListener('online', catchUp);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', catchUp);
      window.removeEventListener('online', catchUp);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, runCheck]);

  useEffect(() => {
    if (!enabled || nextCheckAt === null) return;
    const delay = Math.max(0, nextCheckAt - nowRef.current().getTime());
    const timeoutId = window.setTimeout(() => {
      void runCheck(false);
    }, delay);
    return () => window.clearTimeout(timeoutId);
  }, [enabled, nextCheckAt, runCheck]);

  useEffect(() => {
    if (!enabled) return;
    const expectedConfiguration = getConfigurationKey(
      hasOpenAiKey,
      hasAnthropicKey,
      threshold,
      credentialRevision
    );
    const handleMonitorStorage = (event: StorageEvent) => {
      if (event.key !== MONITOR_STATE_STORAGE_KEY) return;
      const state = readMonitorState();
      const current = nowRef.current();
      if (
        !state
        || state.utcDay !== formatUtcDateKey(current)
        || state.configurationKey !== expectedConfiguration
        || state.lastSuccessAt <= 0
        || state.lastSuccessAt < state.lastAttemptAt
        || state.retryCount !== 0
      ) return;

      const successfulAt = new Date(state.lastSuccessAt);
      setStatus(hasOpenAiKey || hasAnthropicKey ? 'ready' : 'idle');
      setError(null);
      setLastSuccessfulCheck(successfulAt);
      setNextCheckAt(nextScheduledCheckAt(successfulAt));
    };
    window.addEventListener('storage', handleMonitorStorage);
    return () => window.removeEventListener('storage', handleMonitorStorage);
  }, [enabled, hasOpenAiKey, hasAnthropicKey, credentialRevision, threshold]);

  return {
    status,
    error,
    lastSuccessfulCheck,
    checkNow: () => runCheck(true)
  };
}
